import { useContext, useState } from "react"
import {
	Dialog,
	DialogContent,
	Button,
	Typography,
	CircularProgress,
	Box,
} from "@mui/material"
import { MdCheck, MdClose, MdAccessTime, MdLocationOn } from "react-icons/md"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"

export default function GestaoVagaConfirmacaoModal({
	aberto,
	onFechar,
	idVaga,
	idTitular,
	nomeVaga,
	mensagem,
	tipoLembrete = "lembrete_3",
	onConfirmado,
	onRecusado,
}) {
	const { dados } = useContext(ContextoGeral)
	const [processando, setProcessando] = useState(false)
	const [resultado, setResultado] = useState(null)

	const tituloModal =
		tipoLembrete === "lembrete_1"
			? "Confirmação de Presença — Lembrete 1"
			: tipoLembrete === "lembrete_3"
			? "Confirmação de Presença — Lembrete 2"
			: "Confirmação de Presença"

	const handleConfirmar = async () => {
		setProcessando(true)
		setResultado(null)
		try {
			const res = await ServicoAPI.gestaoVagasConfirmarPresencaApp(dados, idVaga, idTitular, tipoLembrete)
			if (res?.sucesso || res?.resultado) {
				setResultado({ tipo: "sucesso", mensagem: res?.mensagem || "Presença confirmada!" })
				onConfirmado?.()
				setTimeout(() => {
					onFechar()
					setResultado(null)
				}, 2000)
			} else {
				setResultado({ tipo: "erro", mensagem: res?.msgUsuario || res?.mensagem || "Erro ao confirmar presença." })
			}
		} catch (err) {
			setResultado({ tipo: "erro", mensagem: "Erro de conexão. Tente novamente." })
		} finally {
			setProcessando(false)
		}
	}

	const handleRecusar = async () => {
		setProcessando(true)
		setResultado(null)
		try {
			const res = await ServicoAPI.gestaoVagasRecusarPresencaApp(dados, idVaga, idTitular, tipoLembrete)
			if (res?.sucesso || res?.resultado) {
				setResultado({
					tipo: "aviso",
					mensagem: res?.mensagem || "Você foi removido da vaga.",
				})
				onRecusado?.()
				setTimeout(() => {
					onFechar()
					setResultado(null)
				}, 2500)
			} else {
				setResultado({ tipo: "erro", mensagem: res?.msgUsuario || res?.mensagem || "Erro ao processar." })
			}
		} catch (err) {
			setResultado({ tipo: "erro", mensagem: "Erro de conexão. Tente novamente." })
		} finally {
			setProcessando(false)
		}
	}

	const mensagemExibida = mensagem || "Você vai participar da jornada de hoje?"

	return (
		<Dialog
			open={aberto}
			onClose={processando ? undefined : onFechar}
			maxWidth="xs"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 4,
					overflow: "hidden",
					mx: 2,
				},
			}}
		>
			{/* Header com ícone */}
			<Box
				sx={{
					background: "linear-gradient(135deg, var(--primaria) 0%, var(--primaria-darker) 100%)",
					py: 3,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Box
					sx={{
						width: 64,
						height: 64,
						borderRadius: "50%",
						bgcolor: "rgba(255,255,255,0.2)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<MdAccessTime size={36} color="#fff" />
				</Box>
				<Typography
					variant="h6"
					sx={{
						color: "#fff",
						fontWeight: 600,
						textAlign: "center",
					}}
				>
					{tituloModal}
				</Typography>
			</Box>

			<DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
				{/* Nome da vaga */}
				{nomeVaga && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mb: 2,
							p: 1.5,
							bgcolor: "#f8f9fa",
							border: "1px solid #e9ecef",
							borderRadius: 2,
						}}
					>
						<MdLocationOn size={20} style={{ color: "#6c757d" }} />
						<Typography variant="subtitle1" fontWeight="600" sx={{ color: "#495057" }}>
							{nomeVaga}
						</Typography>
					</Box>
				)}

				{/* Mensagem */}
				<Typography
					variant="body1"
					sx={{
						textAlign: "center",
						color: "#444",
						lineHeight: 1.6,
						mb: 1,
					}}
				>
					{mensagemExibida}
				</Typography>

				{/* Resultado */}
				{resultado && (
					<Box
						sx={{
							mt: 2,
							p: 2,
							borderRadius: 2,
							textAlign: "center",
							bgcolor:
								resultado.tipo === "sucesso"
									? "#e8f5e9"
									: resultado.tipo === "aviso"
									? "#fff3e0"
									: "#ffebee",
							color:
								resultado.tipo === "sucesso"
									? "#2e7d32"
									: resultado.tipo === "aviso"
									? "#e65100"
									: "#c62828",
						}}
					>
						<Typography variant="body2" fontWeight="500">
							{resultado.mensagem}
						</Typography>
					</Box>
				)}

				{/* Loading */}
				{processando && (
					<Box display="flex" justifyContent="center" py={2}>
						<CircularProgress size={32} sx={{ color: "var(--primaria)" }} />
					</Box>
				)}
			</DialogContent>

			{/* Botões */}
			{!resultado && (
				<Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
					<Button
						fullWidth
						variant="outlined"
						onClick={handleRecusar}
						disabled={processando}
						sx={{
							py: 1.5,
							borderRadius: 3,
							borderColor: "#ef5350",
							color: "#ef5350",
							fontWeight: 600,
							textTransform: "none",
							fontSize: "0.95rem",
							"&:hover": {
								borderColor: "#d32f2f",
								bgcolor: "#ffebee",
							},
						}}
						startIcon={<MdClose size={20} />}
					>
						Não vou
					</Button>
					<Button
						fullWidth
						variant="contained"
						onClick={handleConfirmar}
						disabled={processando}
						sx={{
							py: 1.5,
							borderRadius: 3,
							bgcolor: "#4caf50",
							fontWeight: 600,
							textTransform: "none",
							fontSize: "0.95rem",
							boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
							"&:hover": {
								bgcolor: "#43a047",
							},
						}}
						startIcon={<MdCheck size={20} />}
					>
						Vou participar
					</Button>
				</Box>
			)}
		</Dialog>
	)
}
